<template>
  <div class="st-icon" :aria-hidden="ariaHidden">
    <component
      :is="dynamicComponent"
      :aria-label="alt"
      :size="iconSize"
      v-bind="iconProps"
    />
  </div>
</template>

<script setup>
import { defineProps, computed, inject, ref } from "vue";
import * as StIcons from "./st-icons.js";
import * as BtIcons from "./bt-icons.js";
import * as OtIcons from "./ot-icons.js";
const components = { ...StIcons, ...BtIcons, ...OtIcons };

const props = defineProps({
  name: {
    type: String,
    required: true,
    validator(value) {
      const deprecated = [
        "ticket",
        "add-ticket",
        "error-filled-large",
        "prio-high",
        "warning-filled",
        "varning",
        "card",
        "travel-card",
        "checkmark",
        "checkmark-big",
        "new-checkmark",
        "accept",
        "checkmark-unchecked",
        "device",
        "mobile",
        "warning-big",
        "warning-hollow",
        "cloud-upload",
        "onboarding-upload",
        "down",
        "arrow",
        "arrow-down",
        "arrow-left",
        "arrow-right",
        "arrow-curved",
        "dropdown",
        "exchange",
        "link-arrow",
        "ticket-expand",
        "travel-arrow",
        "up",
      ];
      if (deprecated.includes(value)) {
        console.warn(
          `The iconname ${value} is deprecated. See changelog for new name.`,
        );
        return false;
      }
      return true;
    },
  },
  iconProps: {
    type: Object,
  },
  alt: {
    type: String,
  },
  size: {
    type: [String, Number],
    validator(value) {
      const preferredSizes = ["small", "medium", "large"].includes(value);
      if (!preferredSizes) {
        console.warn(
          "This icon has a custom size. Preferably, avoid this — verify with your local UX if necessary.",
        );
      }
      return preferredSizes;
    },
    default: "medium",
  },
  ariaHidden: {
    type: Boolean,
    default: true,
  },
});

const { theme } = inject("theme", { theme: ref("st") });

const dynamicComponent = computed(() => {
  //Name is st-info etc. To be removed when all applications has implemented theme functionality
  const noTheme = componentExist(props.name);
  if (noTheme) return components[convertToPascal(props.name)];

  //Themeable and the theme has the icon.
  const themedIcon = iconName(theme.value);
  const existingThemedIcon = componentExist(themedIcon);
  if (existingThemedIcon) return components[convertToPascal(themedIcon)];

  //Fallback
  const fallbackIcon = iconName("st");
  return components[convertToPascal(fallbackIcon)];
});

const iconSize = computed(() => {
  const sizes = {
    small: 20,
    medium: 24,
    large: 40,
  };
  return sizes[props.size] || props.size;
});
const iconName = (theme) => {
  return `${theme}-${props.name}`;
};
const componentExist = (componentName) => {
  const pascalName = convertToPascal(componentName);
  return Object.prototype.hasOwnProperty.call(components, pascalName);
};
const convertToPascal = (string) => {
  return string
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
};
</script>

<style lang="scss" scoped>
.st-icon {
  display: inline-flex;
}
</style>
